import React from 'react';
import { useGetMerchantFundCollectionPaymentsQuery } from '../../redux/apis/endpoints/paymentApi';
import { formatCurrency } from '../../utils/formatters';
import { CreditCardIcon, CalendarIcon, UserIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const getStatusIcon = (refundStatus) => {
  switch (refundStatus) {
    case 'payment_collected':
    case 'payment_collected_ready_for_refund':
      return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
    case 'merchant_refunded':
      return <CheckCircleIcon className="h-5 w-5 text-blue-500" />;
    case 'merchant_refund_failed':
      return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
    default:
      return <CheckCircleIcon className="h-5 w-5 text-gray-500" />;
  }
};

const getStatusText = (refundStatus) => {
  switch (refundStatus) {
    case 'payment_collected':
      return 'Payment Collected';
    case 'payment_collected_ready_for_refund':
      return 'Ready for Customer Refund';
    case 'merchant_refunded':
      return 'Refunded to You';
    case 'merchant_refund_failed':
      return 'Refund Failed';
    default:
      return 'Payment Collected';
  }
};

const getStatusColor = (refundStatus) => {
  switch (refundStatus) {
    case 'payment_collected':
    case 'payment_collected_ready_for_refund':
      return 'text-green-700 bg-green-50 border-green-200';
    case 'merchant_refunded':
      return 'text-blue-700 bg-blue-50 border-blue-200';
    case 'merchant_refund_failed':
      return 'text-red-700 bg-red-50 border-red-200';
    default:
      return 'text-gray-700 bg-gray-50 border-gray-200';
  }
};

const MerchantFundCollectionPayments = () => {
  const {
    data: paymentsData,
    isLoading,
    error
  } = useGetMerchantFundCollectionPaymentsQuery();

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Refund Fund Payments</h3>
        <div className="flex justify-center py-8">
          <svg className="animate-spin h-6 w-6 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Refund Fund Payments</h3>
        <div className="text-center py-8">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <p className="text-sm text-red-600">Failed to load refund fund payments</p>
        </div>
      </div>
    );
  }

  const payments = paymentsData?.payments || [];

  if (payments.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Refund Fund Payments</h3>
        <div className="text-center py-8">
          <CreditCardIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-sm text-gray-500">No refund fund payments yet</p>
          <p className="text-xs text-gray-400 mt-2">
            Payments made for customer return refunds will appear here
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Refund Fund Payments</h3>
        <span className="text-sm text-gray-500">{payments.length} payment{payments.length !== 1 ? 's' : ''}</span>
      </div>
      
      <div className="space-y-4">
        {payments.map((payment) => (
          <div key={payment.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3 flex-1">
                <div className="flex-shrink-0 mt-1">
                  <CreditCardIcon className="h-5 w-5 text-gray-400" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      Refund Fund Payment
                    </p>
                    <div className="flex items-center ml-2">
                      {getStatusIcon(payment.refundStatus)}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4 mt-1">
                    <p className="text-lg font-semibold text-gray-900">
                      {formatCurrency(payment.amount, payment.currency)}
                    </p>
                    {payment.usageFee > 0 && (
                      <span className="text-sm text-gray-500">
                        Usage fee: {formatCurrency(payment.usageFee, payment.currency)}
                      </span>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                    <div className="flex items-center">
                      <CalendarIcon className="h-4 w-4 mr-1" />
                      {new Date(payment.createdAt).toLocaleDateString()}
                    </div>
                    
                    {payment.customerInfo && (
                      <div className="flex items-center">
                        <UserIcon className="h-4 w-4 mr-1" />
                        {payment.customerInfo.firstName} {payment.customerInfo.lastName}
                      </div>
                    )}
                    
                    {payment.itemsCount > 0 && (
                      <span>
                        {payment.itemsCount} item{payment.itemsCount !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  
                  <div className="mt-2">
                    <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border ${getStatusColor(payment.refundStatus)}`}>
                      {getStatusText(payment.refundStatus)}
                    </span>
                  </div>
                  
                  {payment.description && (
                    <p className="text-sm text-gray-600 mt-2">{payment.description}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="flex justify-between text-sm text-gray-500">
          <span>Total payments made:</span>
          <span className="font-medium">
            {formatCurrency(
              payments.reduce((sum, payment) => sum + payment.amount, 0),
              payments[0]?.currency || 'USD'
            )}
          </span>
        </div>
      </div>
    </div>
  );
};

export default MerchantFundCollectionPayments; 